import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { 
  type ThemeId, 
  type Theme, 
  themes, 
  defaultTheme, 
  themeToCSSVariables 
} from '@/config/themes';

interface ThemeContextType {
  currentTheme: Theme;
  themeId: ThemeId;
  setTheme: (themeId: ThemeId) => void;
  toggleTheme: () => void;
  availableThemes: Theme[];
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const STORAGE_KEY = 'maantt-theme';

// Get theme from localStorage or default
function getInitialTheme(): ThemeId {
  if (typeof window === 'undefined') return defaultTheme;
  
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored && stored in themes) {
    return stored as ThemeId;
  }
  return defaultTheme;
}

// Apply theme CSS variables to document
function applyTheme(theme: Theme) {
  const root = document.documentElement;
  const variables = themeToCSSVariables(theme);
  
  Object.entries(variables).forEach(([property, value]) => {
    root.style.setProperty(property, value);
  });
  
  // Also set a data attribute for potential CSS selectors
  root.setAttribute('data-theme', theme.id);
}

interface ThemeProviderProps {
  children: React.ReactNode;
  defaultThemeId?: ThemeId;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ 
  children, 
  defaultThemeId 
}) => {
  const [themeId, setThemeId] = useState<ThemeId>(() => {
    // During SSR or initial render, use default
    if (typeof window === 'undefined') {
      return defaultThemeId || defaultTheme;
    }
    return getInitialTheme();
  });

  const currentTheme = themes[themeId];

  // Apply theme on mount and when theme changes
  useEffect(() => {
    applyTheme(currentTheme);
    localStorage.setItem(STORAGE_KEY, themeId);
  }, [themeId, currentTheme]);

  // Set theme handler
  const setTheme = useCallback((newThemeId: ThemeId) => {
    if (newThemeId in themes) {
      setThemeId(newThemeId);
    }
  }, []);

  // Toggle to next theme
  const toggleTheme = useCallback(() => {
    const themeIds = Object.keys(themes) as ThemeId[];
    const currentIndex = themeIds.indexOf(themeId);
    const nextIndex = (currentIndex + 1) % themeIds.length;
    setThemeId(themeIds[nextIndex]);
  }, [themeId]);

  const value: ThemeContextType = {
    currentTheme,
    themeId,
    setTheme,
    toggleTheme,
    availableThemes: Object.values(themes),
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

// Hook to use theme context
export function useTheme(): ThemeContextType {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

// Export types
export type { ThemeContextType };
